import * as signalR from "@microsoft/signalr";
import authService from '../../components/api-authorization/AuthorizeService';
import * as Logger from 'loglevel';

class StoryLockClient {
    constructor() {
        this.connection = null;
        this.lockCallbacks = {
            onLockAcquired: null,
            onLockDenied: null,
            onInitialLocks: null,
            onLockReleased: null
        };
    }

    async initialize() {
        // Initialize connection to the StoryLockHub
        const authToken = await authService.getAccessTokenAsync();
        const userId = authService.getCurrentHubUser().id;
        this.connection = new signalR.HubConnectionBuilder()
            .withUrl(`/storyLockHub?userId=${userId}`, { accessTokenFactory: () => authToken })
            .withAutomaticReconnect()
            .build();

        // Set up event handlers
        this.connection.on("LockAcquired", storyId => {
            Logger.debug("Lock acquired for story:", storyId);
            if (this.lockCallbacks.onLockAcquired) { 
                this.lockCallbacks.onLockAcquired(storyId);
            }
        });

        this.connection.on("LockDenied", (storyId, userId) => {
            Logger.debug("Lock denied for story:", storyId, "Already locked by user:", userId);
            if (this.lockCallbacks.onLockDenied) {  
                this.lockCallbacks.onLockDenied(storyId, userId);
            }
        });

        this.connection.on("LockReleased", (storyId) => {
            Logger.debug("Lock released for story:", storyId);
            if (this.lockCallbacks.onLockReleased) {  
                this.lockCallbacks.onLockReleased(storyId);
            }
        });

        // This one is already correct:
        this.connection.on("InitialLocks", (lockDictionary) => {
            Logger.debug("Received initial locks:", lockDictionary);
            if (this.lockCallbacks.onInitialLocks) {
                this.lockCallbacks.onInitialLocks(lockDictionary);
            }
        });
        // Start the connection
        try {
            await this.connection.start();
            Logger.debug("StoryLockHub connection established");

            //// Fetch current locks after connection is established
            //const currentLocks = await this.getCurrentLocks();
            //Logger.debug("Retrieved current locks:", currentLocks);

            //// Notify about existing locks
            //if (this.onInitialLocks && currentLocks) {
            //    this.onInitialLocks(currentLocks);
            //}

            return true;
        } catch (err) {
            Logger.error("Error establishing StoryLockHub connection:", err);
            return false;
        }
    }

    async disconnect() {
        if (this.connection) {
            try {
                await this.connection.stop();
                Logger.debug("StoryLockHub connection disconnected");
            } catch (err) {
                Logger.error("Error disconnecting from StoryLockHub:", err);
            }
        }
    }

    async stop() {
        return await this.disconnect();
    }

    setLockCallbacks(callbacks) {
        this.lockCallbacks = {
            onLockAcquired: null,
            onLockDenied: null,
            onInitialLocks: null,
            onLockReleased: null,
            ...callbacks
        };
    }

    async getCurrentLocks() {
        if (!this.connection || this.connection.state !== signalR.HubConnectionState.Connected) {
            Logger.error("Cannot get locks: StoryLockHub connection not established");
            return null;
        }

        try {
            return await this.connection.invoke("GetCurrentLocks");
        } catch (err) {
            Logger.error("Error getting current locks:", err);
            return null;
        }
    }

    getConnectionId() {
        return this.connection ? this.connection.connectionId : null;
    }

    async requestLock(storyId, userId) {
        if (!this.connection || this.connection.state !== signalR.HubConnectionState.Connected) {
            Logger.error("Cannot request lock: StoryLockHub connection not established");
            return false;
        }
        try {
            return await this.connection.invoke("RequestLock", storyId, userId);
        } catch (err) {
            Logger.error("Error requesting lock:", err);
            return false;
        }
    }

    async releaseLock(storyId) {
        if (!this.connection || this.connection.state !== signalR.HubConnectionState.Connected) {
            Logger.error("Cannot release lock: StoryLockHub connection not established");
            return false;
        }

        try {
            await this.connection.invoke("ReleaseLock", storyId);
            return true;
        } catch (err) {
            Logger.error("Error releasing lock:", err);
            return false;
        }
    }
}

const storyLockClient = new StoryLockClient();
export default storyLockClient;